import { useState } from "react";
import axios from "axios";
import useGoogleMapApiKey from "features/shared/useGoogleMapApiKey";

type AddressComponentType =
  | "street_number"
  | "route"
  | "locality"
  | "sublocality"
  | "administrative_area_level_2"
  | "administrative_area_level_1"
  | "country"
  | "postal_code";

interface GeocodeResponse {
  results: {
    address_components: {
      long_name: string;
      short_name: string;
      types: AddressComponentType[];
    }[];
    formatted_address: string;
    geometry: {
      location: {
        lat: number;
        lng: number;
      };
    };
  }[];
}

export function useGoogleGeocoder(enabled?: boolean) {
  const [loading, setLoading] = useState(false);
  const { data, refetch } = useGoogleMapApiKey(enabled);

  const addressToCoordinates = async (address: string) => {
    const doCall = async (key: string) => {
      const { data } = await axios.get<GeocodeResponse>(
        `https://maps.googleapis.com/maps/api/geocode/json`,
        {
          params: {
            address,
            key,
          },
        }
      );

      if (!data.results.length) {
        return;
      }

      const result = data.results[0];
      return {
        latitude: result.geometry.location.lat,
        longitude: result.geometry.location.lng,
      };
    };

    try {
      setLoading(true);
      if (data) {
        return doCall(data);
      } else {
        return refetch().then(({ data: key }) => doCall(key!));
      }
    } catch (err) {
    } finally {
      setLoading(false);
    }
  };

  const coordinatesToAddress = async (latitude: number, longitude: number) => {
    const doCall = async (key: string) => {
      const { data } = await axios.get<GeocodeResponse>(
        `https://maps.googleapis.com/maps/api/geocode/json`,
        {
          params: {
            latlng: `${latitude},${longitude}`,
            key,
          },
        }
      );

      if (!data.results.length) {
        return;
      }

      const result = data.results[0];
      const cep = result.address_components.find((component) =>
        component.types.includes("postal_code")
      )?.long_name;
      const numero = result.address_components.find((component) =>
        component.types.includes("street_number")
      )?.long_name;
      const logradouro = result.address_components.find((component) =>
        component.types.includes("route")
      )?.long_name;
      const bairro = result.address_components.find((component) =>
        component.types.includes("sublocality")
      )?.long_name;
      const estado = result.address_components.find((component) =>
        component.types.includes("administrative_area_level_1")
      )?.long_name;
      const cidade = result.address_components.find((component) =>
        component.types.includes("administrative_area_level_2")
      )?.long_name;

      return {
        cep,
        numero,
        logradouro,
        bairro,
        estado,
        cidade,
      };
    };
    try {
      setLoading(true);
      if (data) {
        return doCall(data);
      } else {
        return refetch().then(({ data: key }) => doCall(key!));
      }
    } catch (err) {
    } finally {
      setLoading(false);
    }
  };

  return {
    addressToCoordinates,
    coordinatesToAddress,
    loading,
  };
}
